import type { WizardData } from '@/src/types';

/**
 * Decides which Details step the wizard mounts: the DSL builder
 * (ExperienceDetailsStep) or the generic form (StepDetails).
 *
 * Pulled out of Wizard.tsx into its own tested function after this exact gate
 * was silently narrowed to wedding-only by a well-intentioned fix (2026-07-26):
 * ExperienceDetailsStep's UI was hardcoded to "Bride"/"Groom" copy, which was
 * genuinely wrong for a birthday — but the fix added `&& occasionType ===
 * 'WEDDING'` to this predicate instead of fixing the component's copy. Since
 * `buildExperienceDsl()` is *only* ever called from ExperienceDetailsStep's
 * submit handler, that one-line change meant no non-wedding occasion could
 * build a DSL at all, silently. Every unit test in both suites still passed —
 * nothing exercises this predicate against a rendered wizard — and it was
 * only caught by clicking through the flow in a browser.
 *
 * DO NOT narrow this predicate to a subset of occasions/templates as a way to
 * fix a UI-copy or UX bug in ExperienceDetailsStep. Fix the component instead
 * (it already receives `data.occasionType` and can and should branch on it —
 * see OCCASION_COPY in ExperienceDetailsStep.tsx). Narrowing this gate is how
 * the regression happened the first time.
 *
 * If you need to change this predicate at all, add a case to
 * wizard-routing.test.ts proving the new behavior, in the same commit.
 */
export function isAiNativeExperience(data: Pick<WizardData, 'customData' | 'templateId'>): boolean {
  const experienceMeta = data.customData?.experience as { aiNative?: boolean } | undefined;
  return experienceMeta?.aiNative === true
    // Fallback for drafts saved before the aiNative flag existed, or where the
    // flag was never set (e.g. a draft started from a stale template list).
    // Every `exp-*` and `video-*` id in StepTemplate.tsx's catalog is an experience template — widening
    // this prefix is safe because it only ever adds templates to the experience details path.
    || Boolean(data.templateId?.startsWith('exp-'))
    || Boolean(data.templateId?.startsWith('video-'));
}

/**
 * Which wizard step to land on when reopening an existing invitation for
 * edit (`/wizard?draftId=...`).
 *
 * A template is never re-picked on entry: if the invitation already has a
 * `templateId`, the floor is Details (step 3), regardless of what
 * `draftMeta.step` says. The user can still click "Template" in the step
 * indicator to change it deliberately.
 *
 * Why the floor matters — this was a real, reproduced bug, not a
 * hypothetical: a published invitation (complete eventName/eventDate/
 * eventVenue/hostName, had clearly gone all the way to Publish) was found
 * with `draftMeta.step: 1` in production data. Trusting that value verbatim
 * landed the user back on "What are we celebrating?" — forcing them to
 * re-pick an occasion and a template they don't remember choosing, just to
 * reach the field they actually wanted to edit. Worse, it was
 * self-reinforcing: simply having the wizard mounted on step 1 for an
 * existing draft is enough to trigger the autosave effect (drafts allow
 * writes at any step, unlike new ones which require step >= 3 to even be
 * created), which re-persists `draftMeta.step: 1` right back to the server —
 * so once any invitation got stuck this way, it stayed stuck on every future
 * edit. Clamping the floor here breaks that loop as well as fixing the
 * immediate landing.
 */
export function resolveEditEntryStep(
  hasTemplateId: boolean,
  draftMetaStep: number | undefined,
): number {
  if (!hasTemplateId) return draftMetaStep ?? 1;
  return Math.max(draftMetaStep ?? 3, 3);
}
